import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  TextField,
  Box,
  InputAdornment,
  IconButton,
  Alert,
  Card,
  CardContent,
} from "@material-ui/core";
import LoadingButton from "@material-ui/lab/LoadingButton";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import Page from "src/components/Page";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import { useClienteActual } from "src/hooks/useClienteActual";
import { useCambiarContrasenaForzadaMutation } from "src/services/accesoClientesApi";

const Schema = Yup.object().shape({
  nuevaContrasena: Yup.string()
    .min(8, "La contraseña debe tener al menos 8 caracteres")
    .required("La contraseña es requerida"),
  confirmarContrasena: Yup.string()
    .oneOf([Yup.ref("nuevaContrasena")], "Las contraseñas no coinciden")
    .required("Debes confirmar la contraseña"),
});

export default function CambiarContrasenaForzado() {
  const navigate = useNavigate();
  const { nombre } = useClienteActual();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [exito, setExito] = useState(false);

  const [cambiarContrasenaForzada] = useCambiarContrasenaForzadaMutation();

  const formik = useFormik({
    initialValues: { nuevaContrasena: "", confirmarContrasena: "" },
    validationSchema: Schema,
    onSubmit: async (_, { setSubmitting }) => {
      setError("");
      try {
        await cambiarContrasenaForzada({
          nuevaContrasena: formik.values.nuevaContrasena,
        }).unwrap();
        setExito(true);
      } catch (err) {
        setError(
          err?.data?.message ||
            "Ha ocurrido un error al actualizar la contraseña. Intenta nuevamente.",
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

  const handleIrAlLogin = () => {
    // Limpiar sesión — el JWT antiguo tiene debeActualizarContrasena=true,
    // no se puede reutilizar. El cliente re-loguea con su nueva contraseña.
    localStorage.clear();
    navigate("/login");
  };

  return (
    <Page title="Actualizar contraseña — Taxtic">
      <Container maxWidth="sm">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="80vh"
        >
          <Card style={{ width: "100%", borderRadius: 12 }} elevation={3}>
            <CardContent style={{ padding: "40px 32px" }}>
              {/* ── Cabecera ──────────────────────────────────────────── */}
              <Box
                display="flex"
                alignItems="center"
                style={{ gap: 10, marginBottom: 8 }}
              >
                <LockOutlinedIcon color="primary" style={{ fontSize: 28 }} />
                <Typography variant="h5" style={{ fontWeight: 700 }}>
                  Actualiza tu contraseña
                </Typography>
              </Box>

              {!exito && (
                <Alert severity="info" style={{ marginBottom: 24 }}>
                  {nombre ? `Hola ${nombre}, por` : "Por"} seguridad, debes
                  establecer una contraseña propia antes de continuar. La
                  contraseña que recibiste por correo es temporal.
                </Alert>
              )}

              {/* ── Estado: éxito ─────────────────────────────────────── */}
              {exito ? (
                <Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    style={{ gap: 8, marginBottom: 16 }}
                  >
                    <CheckCircleOutlineIcon
                      style={{ color: "#2e7d32", fontSize: 32 }}
                    />
                    <Typography
                      variant="h6"
                      style={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      ¡Contraseña actualizada!
                    </Typography>
                  </Box>

                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ marginBottom: 32 }}
                  >
                    Tu contraseña fue actualizada correctamente. Inicia sesión
                    nuevamente para continuar.
                  </Typography>

                  <LoadingButton
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={handleIrAlLogin}
                  >
                    Iniciar sesión
                  </LoadingButton>
                </Box>
              ) : (
                /* ── Estado: formulario ─────────────────────────────────── */
                <FormikProvider value={formik}>
                  <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                    <TextField
                      fullWidth
                      label="Nueva contraseña"
                      type={showPassword ? "text" : "password"}
                      margin="normal"
                      {...getFieldProps("nuevaContrasena")}
                      error={Boolean(
                        touched.nuevaContrasena && errors.nuevaContrasena,
                      )}
                      helperText={
                        touched.nuevaContrasena && errors.nuevaContrasena
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword((v) => !v)}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityIcon />
                              ) : (
                                <VisibilityOffIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Confirmar contraseña"
                      type={showConfirm ? "text" : "password"}
                      margin="normal"
                      {...getFieldProps("confirmarContrasena")}
                      error={Boolean(
                        touched.confirmarContrasena &&
                        errors.confirmarContrasena,
                      )}
                      helperText={
                        touched.confirmarContrasena &&
                        errors.confirmarContrasena
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirm((v) => !v)}
                              edge="end"
                            >
                              {showConfirm ? (
                                <VisibilityIcon />
                              ) : (
                                <VisibilityOffIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    {error && (
                      <Alert severity="error" style={{ marginTop: 16 }}>
                        {error}
                      </Alert>
                    )}

                    <Box mt={3}>
                      <LoadingButton
                        fullWidth
                        type="submit"
                        variant="contained"
                        color="primary"
                        loading={isSubmitting}
                      >
                        Guardar contraseña
                      </LoadingButton>
                    </Box>
                  </Form>
                </FormikProvider>
              )}
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Page>
  );
}
